import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { overtimeApprovalService } from '@/api/domains/overtime/overtimeApproval.service';
import { OVERTIME_FLOW_PROFILE_CODE } from '@/api/domains/attendance/types/attendanceConfiguration.types';
import { colors } from '@/assets/styles/colors';
import { LabelInput } from '@/components/ui/inputs';
import {
  buildOvertimeCounterDefinitions,
  EMPTY_OVERTIME_COUNTERS,
  formatOvertimeHours,
  OVERTIME_STATUS_COLORS,
  resolveOvertimeCounterKeys,
} from '@/hooks/overtime/overtimeApprovalCounters';
import type {
  OvertimeCounterDefinition,
  OvertimeHourFormat,
} from '@/hooks/overtime/overtimeApprovalCounters';
import {
  reformatEditedHours,
  resolveDetailRowHours,
} from '@/hooks/overtime/overtimeApprovalDetailHours';
import { useOvertimeApprovalDetailActions } from '@/hooks/overtime/useOvertimeApprovalDetailActions';
import { useOvertimeJustificationTypes } from '@/hooks/overtime/useOvertimeJustificationTypes';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type {
  PaginatedFetchParams,
  PaginatedFetchResult,
} from '@/hooks/ui/usePaginatedList';
import { useUI } from '@/hooks/ui/useUI';
import { useTranslation } from '@/i18n';
import { formatRut, formatToDdMmYyyy, toCapitalCase } from '@/utils';
import type {
  OvertimeApprovalCounters,
  OvertimeApprovalDetailRow,
  OvertimeCounterKey,
  OvertimeFlowProfileCode,
} from '@/api/domains/overtime/overtimeApproval.types';
import type { BannerPart } from '@/components/ui/banners';
import type { BulkActionItem, TableColumn } from '@/components/ui/tables/Table.types';


const DEFAULT_COUNTER_FILTER: OvertimeCounterKey = 'calculated';

/** Filas por página del detalle: lo que muestra el diseño. */
const DEFAULT_PAGE_SIZE = 8;

/** Ancho fijo de la columna de horas: el input necesita espacio propio y estable. */
const HOURS_COLUMN_WIDTH = 150;

/** Año de referencia arbitrario para resolver el nombre del mes; solo importa el mes. */
const REFERENCE_YEAR_FOR_MONTH_NAMES = 2000;

/**
 * Claves de columna → campos de la whitelist backend `OvertimeApprovalDetailOrderByFields`. Solo
 * `approvedHours` difiere: en el contrato ese campo se llama `justifiedHours` (horas aprobadas, la
 * acepción legada de "justificadas"), y omitir el mapeo hace que el validador backend rechace la
 * consulta.
 */
const ORDER_BY_MAP: Record<string, string> = {
  approvedHours: 'justifiedHours',
};

interface OvertimeApprovalDetailFilters {
  counterFilter: OvertimeCounterKey;
}

/** Descriptor del corte que alimenta el título de la pantalla. */
interface OvertimeApprovalDetailCut {
  year: number;
  month: number;
  description: string | null;
}

function parseEmployeeIds(rawEmployeeIds: string | null): string {
  return (rawEmployeeIds ?? '').trim();
}

function parseMonthlyCutId(rawMonthlyCutId: string | null): number | null {
  if (!rawMonthlyCutId) return null;
  const parsed = Number(rawMonthlyCutId);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatMonthName(month: number): string {
  const date = new Date(REFERENCE_YEAR_FOR_MONTH_NAMES, month - 1, 1);
  return toCapitalCase(date.toLocaleString('es-CL', { month: 'long' }));
}

/**
 * Clave estable de fila. El grano del detalle es (colaborador, jornada, tipo de hora extra), así
 * que ningún campo por sí solo identifica una fila: `employeeId` se repite entre jornadas y
 * `workDate` entre tipos.
 *
 * Hasta ASIS-5355 la clave llevaba además el índice de la fila como desempate, porque el backend
 * solo exponía la etiqueta configurable del tipo (`overtimeType`) y ningún identificador del
 * horario: dos tramos distintos de la misma jornada podían colisionar. Desde ASIS-5356 el detalle
 * trae `scheduleId` y `overtimeTypeCode`, que son exactamente las columnas por las que el SP legado
 * agrupa la jornada, de modo que la cuádrupla ya es única por construcción. Eliminar el índice no
 * es cosmético: la clave debe sobrevivir a un reordenamiento de `rows` y, desde que el detalle se
 * pagina (ASIS-5358), también a un cambio de página — la selección y las horas editadas se guardan
 * por rowId y viajan al backend con esa identidad.
 */
export function buildOvertimeApprovalDetailRowId(row: OvertimeApprovalDetailRow): string {
  return `${row.employeeId}|${row.workDate}|${row.scheduleId ?? 0}|${row.overtimeTypeCode}`;
}

export interface UseOvertimeApprovalDetailResult {
  rows: OvertimeApprovalDetailRow[];
  columns: TableColumn<OvertimeApprovalDetailRow>[];
  getRowId: (row: OvertimeApprovalDetailRow) => string;
  counters: OvertimeCounterDefinition[];
  activeCounterFilter: OvertimeCounterKey;
  onCounterSelect: (key: OvertimeCounterKey) => void;
  isDecimalFormat: boolean;
  onHourFormatToggle: (useDecimal: boolean) => void;
  overtimeApprovalFlowEnabled: boolean;
  isLoading: boolean;
  bulkActions: BulkActionItem[];
  selectedRowIds: Set<string | number>;
  onSelectionChange: (selected: OvertimeApprovalDetailRow[]) => void;
  closedCutMessage: BannerPart[] | null;
  /** Permiso de escritura y corte abierto: gobierna la edición de horas y del comentario. */
  canEditHours: boolean;
  /** Permiso de escritura y corte abierto, sin importar el perfil: gobierna el bloque in-page del
   *  revisor, que puede justificar aunque no pueda editar horas. */
  canSubmit: boolean;
  /** Envío en curso: bloquea la tabla hasta que responde el backend. */
  isSubmitting: boolean;
  comment: string;
  onCommentChange: (value: string) => void;
  isCommentMissing: boolean;
  onApprove: () => Promise<void>;
  /** Reversa las filas seleccionadas y vuelve al listado general (ASIS-5357). */
  onReverse: () => Promise<void>;
  // ── Paginación server-side (ASIS-5358), para todos los perfiles ───────────
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  // ── Vista del revisor (ASIS-5358) ────────────────────────────────────────
  /** Perfil efectivo `REVISOR`: horas de solo lectura y acción Justificar en vez de Aprobar. */
  isReviewer: boolean;
  /** "HHEE : Detalle (Año - Mes - Descripción)"; `undefined` hasta que el corte llega. */
  detailTitle: string | undefined;
  justificationTypeOptions: { value: string; label: string }[];
  isLoadingJustificationTypes: boolean;
  hasNoJustificationTypes: boolean;
  justificationTypeId: number | null;
  onJustificationTypeChange: (justificationTypeId: number | null) => void;
  isJustificationTypeMissing: boolean;
  requiresFreeComment: boolean;
  freeComment: string;
  onFreeCommentChange: (value: string) => void;
  isFreeCommentMissing: boolean;
  onJustify: () => Promise<void>;
}

/**
 * Detalle de horas extras, abierto desde el listado general (`useOvertimeApproval`) para uno o
 * varios colaboradores. Nació de solo lectura (ASIS-5355), desde ASIS-5356 es operativo para el
 * aprobador (columna "Horas a aprobar" editable + Aprobar con comentario obligatorio) y desde
 * ASIS-5358 sirve también al REVISOR: tres contadores, horas de solo lectura y una única acción,
 * Justificar. El perfil lo resuelve el backend (`viewerFlowProfile`) — nunca se infiere aquí.
 *
 * También desde ASIS-5358 la tabla se pagina server-side, para TODOS los perfiles, con búsqueda y
 * ordenamiento que antes no existían. Dos consecuencias que se manejan a propósito:
 * - la selección sobrevive al cambio de página (se guardan las filas, no solo sus ids), y
 * - lo tecleado en "Horas a aprobar" también: `editedHours` solo se descarta cuando cambia el
 *   universo (contador o búsqueda), no al paginar ni al ordenar. Sin eso, pasar de página borraría
 *   en silencio lo que el aprobador escribió.
 *
 * El comportamiento de los contadores (clic en uno filtra y resalta, clic en Calculadas limpia) es
 * EXACTAMENTE el mismo que en el listado general: se reutiliza el helper compartido
 * `overtimeApprovalCounters` en vez de reimplementarlo; lo único que cambia por perfil es el set de
 * claves. A diferencia del listado, aquí el filtro por contador viaja al backend en cada selección
 * porque el endpoint de detalle también resuelve la tabla filtrada por `counterFilter`.
 *
 * La selección y el envío viven en `useOvertimeApprovalDetailActions`, igual que el listado general
 * delega los suyos en `useOvertimeApprovalBulkActions`.
 */
export function useOvertimeApprovalDetail(): UseOvertimeApprovalDetailResult {
  const { t: translate } = useTranslation();
  const { showApiError } = useUI();

  const [searchParams] = useSearchParams();
  const employeeIds = parseEmployeeIds(searchParams.get('employeeIds'));
  const monthlyCutId = parseMonthlyCutId(searchParams.get('monthlyCutId'));

  const [counterValues, setCounterValues] = useState<OvertimeApprovalCounters>(
    EMPTY_OVERTIME_COUNTERS,
  );
  const [overtimeApprovalFlowEnabled, setOvertimeApprovalFlowEnabled] = useState(false);
  const [isMonthlyCutClosed, setIsMonthlyCutClosed] = useState(false);
  const [viewerFlowProfile, setViewerFlowProfile] = useState<OvertimeFlowProfileCode | null>(null);
  const [monthlyCut, setMonthlyCut] = useState<OvertimeApprovalDetailCut | null>(null);
  const [hourFormat, setHourFormat] = useState<OvertimeHourFormat>('HH:MM:SS');
  // rowId → texto tecleado en "Horas a aprobar". Sin entrada aquí la celda muestra el valor que
  // trajo el backend, así que el mapa vacío representa "nada editado" sin duplicar las filas.
  const [editedHours, setEditedHours] = useState<Map<string, string>>(new Map());

  const initialFilters = useMemo<OvertimeApprovalDetailFilters>(
    () => ({ counterFilter: DEFAULT_COUNTER_FILTER }),
    [],
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<OvertimeApprovalDetailFilters>,
      signal: AbortSignal,
    ): Promise<PaginatedFetchResult<OvertimeApprovalDetailRow> | null> => {
      // `enabled` ya lo garantiza; la guarda evita el cast y cubre el instante en que el query
      // string cambia antes de que el hook se re-habilite.
      if (employeeIds === '' || monthlyCutId === null) return null;

      const response = await overtimeApprovalService.getDetail(
        {
          monthlyCutId,
          employeeIds,
          counterFilter: params.filters.counterFilter,
          page: params.page,
          pageSize: params.pageSize,
          searchText: params.searchText,
          orderBy: params.sort?.key,
          orderDirection: params.sort?.direction,
        },
        signal,
      );

      if (!response.success || !response.data) {
        if (response.error?.code === 'ABORTED') return null;
        setCounterValues(EMPTY_OVERTIME_COUNTERS);
        setOvertimeApprovalFlowEnabled(false);
        setIsMonthlyCutClosed(false);
        setViewerFlowProfile(null);
        setMonthlyCut(null);
        showApiError(response, translate('attendance:overtimeApproval.errors.loadFailed'));
        return null;
      }

      setCounterValues(response.data.counters);
      setOvertimeApprovalFlowEnabled(response.data.overtimeApprovalFlowEnabled);
      setIsMonthlyCutClosed(response.data.isMonthlyCutClosed);
      setViewerFlowProfile(response.data.viewerFlowProfile);
      setMonthlyCut({
        year: response.data.monthlyCutYear,
        month: response.data.monthlyCutMonth,
        description: response.data.monthlyCutDescription,
      });

      return { items: response.data.items, totalRecords: response.data.totalRecords };
    },
    [employeeIds, monthlyCutId, showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    refetch,
    debouncedSearchText,
  } = usePaginatedList<OvertimeApprovalDetailRow, OvertimeApprovalDetailFilters>({
    fetchFn,
    initialFilters,
    initialPageSize: DEFAULT_PAGE_SIZE,
    orderByMap: ORDER_BY_MAP,
    // Sin colaboradores o sin corte no hay nada que consultar: la pantalla se abre siempre desde el
    // listado general, que pone ambos en el query string.
    enabled: employeeIds !== '' && monthlyCutId !== null,
  });

  const counterFilter = filters.counterFilter;

  // Identidad del universo consultado. Cambiarlo invalida tanto la selección como lo tecleado:
  // conservarlos contra otra data podría aprobar un valor que el usuario nunca vio junto a esa
  // fila. Paginar y ordenar NO lo cambian, y por eso ambos sobreviven.
  const universeKey = `${counterFilter}|${debouncedSearchText}`;

  useEffect(() => {
    setEditedHours(new Map());
  }, [universeKey]);

  const onCounterSelect = useCallback((key: OvertimeCounterKey) => {
    setFilters((previous) => (
      previous.counterFilter === key ? previous : { ...previous, counterFilter: key }
    ));
  }, [setFilters]);

  // Cambiar de formato reformatea lo ya tecleado en vez de descartarlo: el usuario no debe perder
  // ediciones por mirar las horas en el otro formato.
  const onHourFormatToggle = useCallback((useDecimal: boolean) => {
    const nextFormat: OvertimeHourFormat = useDecimal ? 'DECIMAL' : 'HH:MM:SS';
    if (nextFormat === hourFormat) return;

    setEditedHours((previous) => reformatEditedHours(previous, hourFormat, nextFormat));
    setHourFormat(nextFormat);
  }, [hourFormat]);

  const getRowId = useCallback(
    (row: OvertimeApprovalDetailRow) => buildOvertimeApprovalDetailRowId(row),
    [],
  );

  const onHoursChange = useCallback((rowId: string, value: string) => {
    setEditedHours((previous) => {
      const next = new Map(previous);
      next.set(rowId, value);
      return next;
    });
  }, []);

  const isReviewer = viewerFlowProfile === OVERTIME_FLOW_PROFILE_CODE.REVIEWER;

  // El catálogo solo se pide para el revisor: el endpoint está gateado por perfil y devolvería 403
  // a cualquier otro, igual que la pantalla legada, que también lo cargaba solo para él.
  const {
    options: justificationTypeOptions,
    isLoading: isLoadingJustificationTypes,
    isEmpty: hasNoJustificationTypes,
    requiresFreeComment: requiresFreeCommentFor,
  } = useOvertimeJustificationTypes(isReviewer);

  const {
    bulkActions,
    selectedRowIds,
    onSelectionChange,
    canEditHours,
    canSubmit,
    closedCutMessage,
    isSubmitting,
    comment,
    onCommentChange,
    isCommentMissing,
    onApprove,
    onReverse,
    justificationTypeId,
    onJustificationTypeChange,
    isJustificationTypeMissing,
    requiresFreeComment,
    freeComment,
    onFreeCommentChange,
    isFreeCommentMissing,
    onJustify,
  } = useOvertimeApprovalDetailActions({
    rows,
    monthlyCutId,
    isMonthlyCutClosed,
    getRowId,
    editedHours,
    hourFormat,
    isReviewer,
    requiresFreeCommentFor,
    hasNoJustificationTypes,
    selectionResetKey: universeKey,
    refetch,
  });

  const counters = useMemo<OvertimeCounterDefinition[]>(() => buildOvertimeCounterDefinitions({
    translate,
    counterValues,
    activeCounterFilter: counterFilter,
    hourFormat,
    counterKeys: resolveOvertimeCounterKeys(viewerFlowProfile),
  }), [translate, counterValues, counterFilter, hourFormat, viewerFlowProfile]);

  const detailTitle = useMemo<string | undefined>(() => {
    if (monthlyCut === null) return undefined;

    return translate('attendance:overtimeApproval.detail.title', {
      year: monthlyCut.year,
      month: formatMonthName(monthlyCut.month),
      description: monthlyCut.description ?? '',
    });
  }, [monthlyCut, translate]);

  /* Columna de horas: editable para el aprobador (input dentro del `render`, con el estado en este
     hook) y de SOLO LECTURA para el revisor. El mockup la muestra subrayada como si fuera
     editable, pero el criterio de aceptación y la pantalla legada coinciden en que el revisor no
     modifica horas, así que ni se monta el input. */
  const hoursColumn = useMemo<TableColumn<OvertimeApprovalDetailRow>>(() => {
    if (isReviewer) {
      return {
        key: 'hoursToJustify',
        header: translate('attendance:overtimeApproval.detail.table.hoursToJustify'),
        width: HOURS_COLUMN_WIDTH,
        align: 'center',
        render: (row: OvertimeApprovalDetailRow) => (
          formatOvertimeHours(row.hoursToApprove, hourFormat)
        ),
      };
    }

    return {
      // `Table` no tiene celda editable nativa. Ancho explícito y `wrap` para que el truncado por
      // defecto de la celda no recorte el input.
      key: 'hoursToApprove',
      header: translate('attendance:overtimeApproval.detail.table.hoursToApprove'),
      width: HOURS_COLUMN_WIDTH,
      align: 'center',
      overflowStrategy: 'wrap',
      render: (row: OvertimeApprovalDetailRow) => {
        const rowId = buildOvertimeApprovalDetailRowId(row);
        const hours = resolveDetailRowHours(row, editedHours.get(rowId), hourFormat);

        return (
          <LabelInput
            value={hours.text}
            onChange={(value: string) => onHoursChange(rowId, value)}
            disabled={!canEditHours}
            color={hours.isInvalid ? 'error' : 'default'}
            /* `color="error"` deja el texto en el gris estándar; aquí el criterio de aceptación
               pide el valor mismo en rojo cuando supera las horas calculadas. */
            customColors={hours.isInvalid ? { text: colors.feedback.error300 } : undefined}
            /* `align: 'center'` centra la celda, pero el input es un bloque con ancho propio: sin
               esto el valor queda pegado a la izquierda dentro del recuadro. */
            inputClassName="text-center"
            aria-label={translate('attendance:overtimeApproval.detail.table.hoursToApprove')}
            data-testid={`overtime-approval-detail-hours-input-${rowId}`}
          />
        );
      },
    };
  }, [isReviewer, translate, hourFormat, editedHours, canEditHours, onHoursChange]);

  const columns = useMemo<TableColumn<OvertimeApprovalDetailRow>[]>(() => [
    {
      key: 'rut',
      header: translate('attendance:overtimeApproval.table.rut'),
      sortable: true,
      render: (row: OvertimeApprovalDetailRow) => formatRut(row.rut),
    },
    {
      key: 'employeeName',
      header: translate('attendance:overtimeApproval.table.employee'),
      sortable: true,
    },
    {
      key: 'workDate',
      header: translate('attendance:overtimeApproval.detail.table.workday'),
      sortable: true,
      render: (row: OvertimeApprovalDetailRow) => formatToDdMmYyyy(row.workDate),
    },
    {
      key: 'overtimeType',
      header: translate('attendance:overtimeApproval.detail.table.type'),
      sortable: true,
    },
    {
      key: 'calculatedHours',
      header: translate('attendance:overtimeApproval.table.calculated'),
      sortable: true,
      render: (row: OvertimeApprovalDetailRow) => (
        formatOvertimeHours(row.calculatedHours, hourFormat)
      ),
    },
    {
      // Se mantiene también en la vista del revisor (en 00:00:00 mientras nadie apruebe), tal como
      // el mockup.
      key: 'approvedHours',
      header: translate('attendance:overtimeApproval.table.approved'),
      sortable: true,
      render: (row: OvertimeApprovalDetailRow) => (
        formatOvertimeHours(row.approvedHours, hourFormat)
      ),
    },
    hoursColumn,
    {
      // Para el revisor el estado es el SUYO (Justificada/Pendiente), no el de la aprobación.
      // Ordenable en ambas vistas: el backend resuelve 'status' por la marca del revisor cuando el
      // perfil efectivo es REVISOR y por las horas aprobadas cuando es el aprobador, así que en los
      // dos casos ordena por el mismo dato que la celda muestra.
      key: 'status',
      header: translate('attendance:overtimeApproval.table.status'),
      sortable: true,
      render: (row: OvertimeApprovalDetailRow) => {
        const status = isReviewer ? (row.reviewerStatus ?? row.status) : row.status;

        return (
          <span style={{ color: OVERTIME_STATUS_COLORS[status], fontWeight: 600 }}>
            {status}
          </span>
        );
      },
    },
  ], [translate, hourFormat, hoursColumn, isReviewer]);

  return {
    rows,
    columns,
    getRowId,
    counters,
    activeCounterFilter: counterFilter,
    onCounterSelect,
    isDecimalFormat: hourFormat === 'DECIMAL',
    onHourFormatToggle,
    overtimeApprovalFlowEnabled,
    isLoading: loading,
    bulkActions,
    selectedRowIds,
    onSelectionChange,
    closedCutMessage,
    canEditHours,
    canSubmit,
    isSubmitting,
    comment,
    onCommentChange,
    isCommentMissing,
    onApprove,
    onReverse,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    isReviewer,
    detailTitle,
    justificationTypeOptions,
    isLoadingJustificationTypes,
    hasNoJustificationTypes,
    justificationTypeId,
    onJustificationTypeChange,
    isJustificationTypeMissing,
    requiresFreeComment,
    freeComment,
    onFreeCommentChange,
    isFreeCommentMissing,
    onJustify,
  };
}

export default useOvertimeApprovalDetail;
